import { perf } from "./perf";

export const BUBBLE_COLORS = ["#4FC3F7", "#BA68C8", "#FF4081", "#FF5252", "#7E57C2", "#F48FB1", "#81C784", "#FFCA28", "#29B6F6", "#FF7043"];
const S = 128;
const rand = (a, b) => a + Math.random() * (b - a);
const rgba = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

function sprite(hex, glow) {
  const c = document.createElement("canvas");
  c.width = c.height = 2 * S + 48;
  const g = c.getContext("2d");
  g.translate(S + 24, S + 24);
  if (glow) {
    g.shadowColor = hex;
    g.shadowBlur = 36;
    g.fillStyle = rgba(hex, 0.35);
    g.beginPath();
    g.arc(0, 0, S, 0, Math.PI * 2);
    g.fill();
    return c;
  }
  const fill = g.createRadialGradient(0, 0, S * 0.15, 0, 0, S);
  fill.addColorStop(0, rgba(hex, 0.06));
  fill.addColorStop(0.7, rgba(hex, 0.2));
  fill.addColorStop(1, rgba(hex, 0.5));
  g.fillStyle = fill;
  g.beginPath();
  g.arc(0, 0, S, 0, Math.PI * 2);
  g.fill();
  const rim = g.createConicGradient ? g.createConicGradient(0, 0, 0) : g.createLinearGradient(-S, -S, S, S);
  [[0, hex], [0.25, "#FF80DF"], [0.5, "#80FFFF"], [0.75, "#FFE08A"], [1, hex]].forEach(([o, col]) => rim.addColorStop(o, col));
  g.strokeStyle = rim;
  g.globalAlpha = 0.85;
  g.lineWidth = 5;
  g.beginPath();
  g.arc(0, 0, S - 3, 0, Math.PI * 2);
  g.stroke();
  g.globalAlpha = 1;
  [[-0.38, -0.42, 0.45, 0.9], [0.36, 0.4, 0.16, 0.35]].forEach(([x, y, r, a]) => {
    const h = g.createRadialGradient(x * S, y * S, 0, x * S, y * S, Math.max(0.5, r * S));
    h.addColorStop(0, `rgba(255,255,255,${a})`);
    h.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = h;
    g.fillRect(-S, -S, 2 * S, 2 * S);
  });
  return c;
}

export function createBubbleScene(canvas, { onPop }) {
  const ctx = canvas.getContext("2d");
  const sprites = BUBBLE_COLORS.map((c) => ({ c, n: sprite(c, false), g: sprite(c, true) }));
  let W = 0, H = 0, dpr = 1, raf = 0, last = performance.now(), t = 0, hovered = -1, pops = 0;
  let bubbles = [], parts = [], feelings = [];
  const par = { x: 0, y: 0 };
  const ptr = { x: 0, y: 0 };

  const spawn = (b, fresh) => {
    const z = rand(0.5, 1.3);
    Object.assign(b, { z, r: rand(16, 52) * z * (W < 600 ? 0.75 : 1), x: rand(0, W), y: fresh ? rand(0, H) : H + rand(60, 160), vy: -(10 + 16 * z), ph: rand(0, 6.28), glow: 0 });
    b.sp = sprites[Math.floor(Math.random() * sprites.length)];
  };
  const pos = (b) => ({ x: b.x + par.x * 40 * b.z, y: b.y + par.y * 28 * b.z });

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 600 ? 1.5 : 2);
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
  }

  function hit(x, y) {
    for (let i = bubbles.length - 1; i >= 0; i--) {
      const p = pos(bubbles[i]);
      if (Math.hypot(p.x - x, p.y - y) < bubbles[i].r) return i;
    }
    return -1;
  }

  function frame(now) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    t += dt;
    if (dt > 0) perf.fps += (Math.min(120, 1 / dt) - perf.fps) * 0.05;
    par.x += ((ptr.x - W / 2) / W - par.x) * Math.min(1, dt * 2);
    par.y += ((ptr.y - H / 2) / H - par.y) * Math.min(1, dt * 2);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    bubbles.forEach((b, i) => {
      b.y += b.vy * dt;
      b.x += Math.sin(t * 0.5 + b.ph) * 10 * dt * b.z;
      if (b.y < -b.r * 2) spawn(b, false);
      b.glow += ((i === hovered ? 1 : 0) - b.glow) * Math.min(1, dt * 6);
      const p = pos(b);
      const r = b.r * (1 + 0.08 * b.glow) * (1 + 0.03 * Math.sin(t * 1.3 + b.ph));
      const k = r / S;
      const o = (S + 24) * k;
      if (b.glow > 0.02) {
        ctx.globalAlpha = b.glow;
        ctx.drawImage(b.sp.g, p.x - o, p.y - o, 2 * o, 2 * o);
      }
      ctx.globalAlpha = 0.9;
      ctx.drawImage(b.sp.n, p.x - o, p.y - o, 2 * o, 2 * o);
    });
    for (let i = parts.length - 1; i >= 0; i--) {
      const q = parts[i];
      q.life -= dt / q.dur;
      if (q.life <= 0) {
        parts.splice(i, 1);
        continue;
      }
      ctx.globalAlpha = q.life;
      if (q.ring) {
        ctx.strokeStyle = q.c;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(q.x, q.y, q.r * (1.9 - q.life * 0.9), 0, Math.PI * 2);
        ctx.stroke();
      } else {
        q.x += q.vx * dt;
        q.y += q.vy * dt;
        q.vy += 60 * dt;
        ctx.fillStyle = q.c;
        ctx.beginPath();
        ctx.arc(q.x, q.y, q.s * (0.5 + q.life * 0.5), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    feelings.forEach((f) => {
      if (!f.el) return;
      const x = f.hx * W + Math.sin(t * 0.3 + f.ph) * 12 + par.x * 40 * f.z;
      const y = f.hy * H + Math.cos(t * 0.35 + f.ph) * 10 + par.y * 28 * f.z;
      f.el.style.transform = `translate3d(${(x - f.r).toFixed(1)}px, ${(y - f.r).toFixed(1)}px, 0)`;
    });
    raf = requestAnimationFrame(frame);
  }

  resize();
  bubbles = Array.from({ length: W < 600 ? 14 : 24 }, () => {
    const b = {};
    spawn(b, true);
    return b;
  }).sort((a, b) => a.z - b.z);
  window.addEventListener("resize", resize);
  raf = requestAnimationFrame(frame);

  return {
    setFeelings(list) {
      feelings = list;
    },
    pointerMove(x, y) {
      ptr.x = x;
      ptr.y = y;
      hovered = hit(x, y);
    },
    pop(x, y) {
      const i = hit(x, y);
      if (i < 0) return false;
      const b = bubbles[i];
      const p = pos(b);
      parts.push({ ring: true, x: p.x, y: p.y, r: b.r, c: b.sp.c, life: 1, dur: 0.45 });
      for (let k = 0; k < 16; k++) {
        const a = (k / 16) * Math.PI * 2 + rand(-0.2, 0.2);
        const v = rand(120, 260);
        parts.push({ x: p.x, y: p.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, s: rand(3, 7), c: k % 4 ? b.sp.c : "#ffffff", life: 1, dur: rand(0.7, 1.1) });
      }
      spawn(b, false);
      hovered = -1;
      pops++;
      onPop();
      return true;
    },
    blow() {},
    shimmer() {},
    info() {
      return { pops, hovered, bubbles: bubbles.map((b) => ({ ...pos(b), r: b.r })), fps: Math.round(perf.fps) };
    },
    destroy() {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    },
  };
}
